import { useRootContext } from 'components/RootContainer';
import { FC, ReactNode, createContext, useCallback, useContext } from 'react';
import { MainMenuPermissionKeyType } from '../../@constants/ACL/acl_types';

export interface RootACLContextModel {
  isAllowed: (permission: MainMenuPermissionKeyType) => boolean;
}

export const RootACLContext = createContext<RootACLContextModel>({} as RootACLContextModel);
export const useRootACLContext = () => useContext(RootACLContext);
export const RootACLProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // Get the user information from the root context.
  const { user } = useRootContext();

  const _isAllowed = (
    permission: MainMenuPermissionKeyType,
    userPermissions: Array<string>
  ): boolean => {
    return userPermissions?.some(
      userPermission =>
        permission === userPermission ||
        permission?.startsWith(`${userPermission}.`) ||
        userPermission?.startsWith(`${permission}.`)
    );
  };

  const isAllowed = useCallback(
    (permission: MainMenuPermissionKeyType) => {
      if ((user?.user?.permissions || []).includes('all')) {
        return true;
      }

      const userPermissions: Array<string> = Array.isArray(user?.user?.permissions)
        ? user?.user?.permissions
        : Object.values(user?.user?.permissions) ?? [];

      return _isAllowed(permission, userPermissions);
    },
    [user.user.permissions]
  ); // Dependency array ensures this function is recalculated when user permissions change.

  // Provide the `isAllowed` function to the children via the context.
  return (
    <RootACLContext.Provider
      value={{
        isAllowed,
      }}
    >
      <>{children}</>
    </RootACLContext.Provider>
  );
};

export default RootACLProvider;
